import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { notificationApi } from "~/api/notification";
import {
  AuctionWonEventPayload,
  CardType,
  NftEquipment,
  NftSkill,
  NotificationType,
  OutbidEventPayload,
  SocketEventType,
} from "fungi-types";
import { useStoreUserV2 } from "~/stores/storeUserV2";
import { nanoid } from "nanoid";
import { usePrimaryMarketplace } from "~/stores/storePrimaryMarketplace";
import { NotifType, useStoreApp } from "~/stores/storeApp";
import { useI18n } from "vue-i18n";
import socketService from "~/services/socketService";
import { auctionApi } from "~/api/auction";

enum NotificationStatus {
  READ = "READ",
  SENT = "SENT",
  CREATED = "CREATED",
  FAILURE = "FAILURE",
}

export type Notification = {
  id: string;
  title: string;
  description: string;
  image: string | null;
  link: string | null;
  buttonText: string | null;
  createdAt: Date;
  status: NotificationStatus;
  publicationDate: Date;
  type: NotificationType;
};

const lineBreakRegex = /\n/g;

export const useNotificationCenter = defineStore("notification-center", () => {
  const { t } = useI18n();

  const storeUser = useStoreUserV2();

  const open = ref(false);
  const hideReadNotifications = ref(false);

  const notifications = ref<Notification[]>([]);
  const tempNotifications = ref<Notification[]>([]);

  const allNotifications = computed(() => {
    return [...notifications.value, ...tempNotifications.value].sort(
      (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
    );
  });

  const unreadNotificationsCount = computed(() => {
    return allNotifications.value.filter((n) => n.status !== NotificationStatus.READ).length;
  });

  socketService.on(SocketEventType.AUCTION_WON, async (event: AuctionWonEventPayload) => {
    const notificationTitle = t("messages.bid-win-title");
    const notificationBody = t("messages.bid-win", {
      cardName: event.card.title,
      skill: event.card.skill || "",
      scarcity: event.card.scarcity || "",
    }).replace(/ {2,}/g, " ");

    notifications.value.push({
      id: nanoid(),
      title: notificationTitle,
      description: notificationBody,
      image: event.card.image,
      link: null,
      type: NotificationType.IN_APP,
      buttonText: null,
      createdAt: new Date(event.timestamp),
      publicationDate: new Date(event.timestamp),
      status: NotificationStatus.CREATED,
    });

    useStoreApp().displayNotification(NotifType.WIN, event);

    await showPushNotification({
      title: notificationTitle,
      body: notificationBody,
      image: event.card.image,
      timestamp: new Date(event.timestamp).getTime(),
      vibrate: [100, 50, 100],
    });
  });

  socketService.on(SocketEventType.USER_OUTBID, async (event: OutbidEventPayload) => {
    let auction = usePrimaryMarketplace().auctions.find(
      (a) => a.auction?.cuid === event.auctionCuid
    );
    if (!auction) {
      const resp = await auctionApi.auctions.list.query({
        auctionCuids: [event.auctionCuid],
        limit: 1,
      });
      auction = resp.result[0];
    }
    if (!auction) {
      console.error(`Auction ${event.auctionCuid} not found in primary marketplace`);
      return;
    }

    const nftSkill = auction?.cardType === CardType.NftSkills ? (auction as NftSkill) : null;
    const nftEquipment =
      auction?.cardType === CardType.NftEquipment ? (auction as NftEquipment) : null;

    const cardImage = nftSkill?.image || (nftEquipment?.image as string);

    const notificationTitle = t("messages.outbid-notification-title", "You've been outbid");
    const notificationBody =
      nftSkill !== null
        ? t("messages.outbid", {
            cardName: `${nftSkill.firstName} ${nftSkill.lastName}`,
            skill: nftSkill.skill,
            scarcity: nftSkill.scarcity,
          })
        : t("messages.outbid-equipment", {
            cardName: nftEquipment!.title,
          });

    notifications.value.push({
      id: nanoid(),
      title: notificationTitle,
      description: notificationBody,
      image: cardImage,
      link: null,
      type: NotificationType.IN_APP,
      buttonText: null,
      createdAt: new Date(event.timestamp),
      publicationDate: new Date(event.timestamp),
      status: NotificationStatus.CREATED,
    });

    if (auction) {
      useStoreApp().displayNotification(NotifType.OUTBID, auction);
      // Update user balance
      await useStoreUserV2().refreshBalance();
    } else {
      console.error(`Auction ${event.auctionCuid} not found in primary marketplace`);
    }

    await showPushNotification({
      title: notificationTitle,
      body: notificationBody,
      image: cardImage,
      timestamp: new Date(event.timestamp).getTime(),
      vibrate: [100, 50, 100],
    });
  });

  socketService.on(SocketEventType.NOTIFICATIONS_REFRESH, async () => {
    console.info(`Refreshing notifications`);
    fetchNotifications();
  });

  async function requestPushNotificationsPermission(): Promise<typeof Notification.permission> {
    return await Notification.requestPermission();
  }

  async function showPushNotification(params: NotificationOptions & { title: string }) {
    const shouldShowPushNotification = (await requestPushNotificationsPermission()) === "granted";

    const { title, ...otherParams } = params;

    if (shouldShowPushNotification) {
      new Notification(title, otherParams);
    }
  }

  setInterval(() => {
    if (!storeUser.isConnected) return;

    fetchNotifications();
  }, 5 * 60_000);

  async function fetchNotifications() {
    try {
      notifications.value = (
        await notificationApi.inApp.list.query({
          showRead: !hideReadNotifications.value,
        })
      )
        .map((n) => ({
          ...n,
          description: n.description.replace(lineBreakRegex, "<br />"),
          publicationDate: new Date(n.publicationDate),
          createdAt: new Date(n.publicationDate ?? n.createdAt),
        }))
        .filter((n) => {
          return n.publicationDate.getTime() < Date.now();
        });
    } catch (error) {
      console.log(`❌ Failed to fetching notifications`, error);
    }
  }

  async function markAllAsRead() {
    await notificationApi.inApp.markAllAsRead.mutate();

    notifications.value = notifications.value.map((n) => ({
      ...n,
      status: NotificationStatus.READ,
    }));
    tempNotifications.value = tempNotifications.value.map((n) => ({
      ...n,
      status: NotificationStatus.READ,
    }));
  }

  async function markAsRead(id: string) {
    if (notifications.value.some((n) => n.id === id)) {
      await notificationApi.inApp.updateStatus.mutate({
        id,
        status: NotificationStatus.READ,
      });

      notifications.value = notifications.value.map((n) => ({
        ...n,
        status: n.id === id ? NotificationStatus.READ : n.status,
      }));

      console.log(notifications.value);
    } else {
      tempNotifications.value = tempNotifications.value.map((n) => ({
        ...n,
        status: n.id === id ? NotificationStatus.READ : n.status,
      }));
    }
  }

  return {
    open,
    notifications,
    tempNotifications,
    allNotifications,
    hideReadNotifications,
    unreadNotificationsCount,
    requestPushNotificationsPermission,
    fetchNotifications,
    markAllAsRead,
    markAsRead,
  };
});
